// ── Adapter Supabase do IInviteRepository ─────────────────────────
// Única camada que conhece as RPCs de convite/acesso e as tabelas
// invites/profiles.

import { getSupabase } from '@infra/supabase/client';
import type {
  IInviteRepository,
  InviteLookup,
  PendingInvite,
  PendingProfile,
  AccessRpcResult,
  CreateInviteParams,
} from '@modules/invites';

const requireSupabase = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseInviteRepository implements IInviteRepository {
  lookupInvite = async (token: string): Promise<InviteLookup> => {
    const { data, error } = await requireSupabase().rpc('lookup_invite', { p_token: token });
    if (error) throw error;
    return (data ?? null) as unknown as InviteLookup;
  };

  claimInvite = async (token: string): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('claim_invite', { p_token: token });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };

  acceptInvite = async (token: string, nome: string, avatarUrl?: string): Promise<InviteLookup> => {
    const { data, error } = await requireSupabase().rpc('complete_invite_v2', {
      p_token: token,
      p_nome: nome,
      p_avatar_url: avatarUrl,
    });
    if (error) throw error;
    return (data ?? null) as unknown as InviteLookup;
  };

  listPendingInvites = async (workspaceId: string): Promise<PendingInvite[]> => {
    const { data, error } = await requireSupabase()
      .from('invites')
      .select('*, creator:profiles!invites_created_by_fkey ( id, nome )')
      .eq('workspace_id', workspaceId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as unknown as PendingInvite[];
  };

  listPendingProfiles = async (): Promise<PendingProfile[]> => {
    const { data, error } = await requireSupabase()
      .from('profiles')
      .select(`
        id, nome, initials, avatar_url, pendente, ativo, created_at,
        workspace_members!workspace_members_user_id_fkey (
          workspace_id, role, ativo
        )
      `)
      .eq('pendente', true);
    if (error) throw error;
    return (data || []) as unknown as PendingProfile[];
  };

  createInvite = async ({ workspaceId, teamRole, email, timeId }: CreateInviteParams): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('create_invite', {
      p_workspace_id: workspaceId,
      p_team_role:    teamRole as 'sdr' | 'closer' | 'gestor' | 'aex',
      p_email:        email?.trim() || undefined,
      p_time_id:      timeId || undefined,
    });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };

  createReusableAexInvite = async (workspaceId: string): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('create_reusable_aex_invite', {
      p_workspace_id: workspaceId,
    });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };

  cancelInvite = async (inviteId: string): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('cancel_invite', { p_invite_id: inviteId });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };

  approveUser = async (userId: string): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('approve_user', { p_user_id: userId });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };

  rejectUser = async (userId: string): Promise<AccessRpcResult> => {
    const { data, error } = await requireSupabase().rpc('reject_user', { p_user_id: userId });
    if (error) throw error;
    return (data ?? null) as unknown as AccessRpcResult;
  };
}
